import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, map } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { SubscriptionDataSourceClient } from '../../../shared/abstracts/services/subscription-management.service';
import { User } from '../../models/user.model';

const jsonHttpOptions = {
  headers: new HttpHeaders({
    Accept: 'application/json'
  })
};

@Injectable()
export class UserService implements SubscriptionDataSourceClient<string, User> {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.legacyFlyAdminApi}/user`;
  private readonly userCache: Map<string, Observable<User | null>> = new Map();

  getById(id: string): Observable<User> {
    return this.retrieveUser(id).pipe(
      map((user) => {
        if (user == null) {
          throw new Error(`Retrieve User by customerGuid ${id} was null`);
        }
        return user;
      })
    );
  }

  /**
   * Retrieves customer information from flyAdmin.
   * Setting createUnknownCustomerIfNotFound to true will return 'Unknown' values if the Garmin IT customer is not found.
   * Only use createUnknownCustomerIfNotFound for critical pieces of the application
   * such as managing an aircraft, transfers, etc. This will allow the features to still work properly
   * if the customerGuid associated to the data is not found in the ITs system.
   *
   * @param customerGuid customerGuid of the user to retrieve
   * @param createUnknownCustomerIfNotFound returns mocked data if the customerGuid does not exist in ITs system
   * @returns User
   */
  retrieveUser(customerGuid: string, createUnknownCustomerIfNotFound: boolean = false): Observable<User | null> {
    let userToReturn = this.userCache.get(customerGuid);
    if (userToReturn == null) {
      let params = new HttpParams();
      if (createUnknownCustomerIfNotFound != null) {
        params = params.set('createUnknownCustomerIfNotFound', createUnknownCustomerIfNotFound);
      }

      userToReturn = this.http.get<User>(`${this.url}/${customerGuid}/`, { ...jsonHttpOptions, params: params }).pipe(
        tap((user) => {
          this.userCache.set(customerGuid, of(user));
        }),
        catchError(this.handleError<null>(null))
      );
    }
    return userToReturn;
  }

  searchUsers(searchParameter: string): Observable<User[] | null> {
    return this.http
      .post<User[]>(`${this.url}/search`, { query: searchParameter }, jsonHttpOptions)
      .pipe(catchError(this.handleError<null>(null)));
  }

  private handleError<T>(result: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result);
    };
  }
}
